import React, { useState, useEffect, useRef } from "react";

import {
  Autocomplete,
  Typography,
  TextField,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  FormLabel,
  InputAdornment,
  CircularProgress,
  Tooltip,
  IconButton,
  OutlinedInput,
  FormHelperText,
  ListItemText,
} from "@mui/material";
import es from "date-fns/locale/es";

//components @mui/x-date-pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import {
  ObtenerListadoRegistrosGenericos,
  ObtenerListadoRegistrosTablaSistema,
} from "../../../Actions/Utilidades/CodigosSistemaAction";
import { ObtenerListadoCamposDinamicosRadios } from "../../../Actions/Utilidades/CamposDinamicosAction";
import { useStateValue } from "../../../Contexto/store";
import InputMask from "react-input-mask";
import AccionesUsuario from "../../../Configuracion/Llaves/AccionesUsuario";
import { enlistaArrayCodigosMensajesIdiomas } from "../../../Actions/Generales/IdiomasAction";
import { MostrarMensaje } from "../../../Utilidades/ManejoMensajes";
import { getFormatDateLocalStorage } from "../../../Utilidades/LeerInfoLocalStorage";
import {
  DescargarArchivoBase64,
  RutinaLeerArchivoInputFile,
  ValidarFormatoArchivo,
} from "../../../Utilidades/ManejoArchivos";

//Material Icons
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import {
  BajarArchivosServidor,
  EliminarArchivosServidor,
  SubirArchivosServidor,
} from "../../../Actions/Utilidades/ManejoArchivosAction";

const idioma = localStorage.getItem("etiqueta_idioma") || "ES";
const NULL_GUID = "00000000-0000-0000-0000-000000000000";
const SYSTEM_TABLE_PAGE_SIZE = 10;

const normalizeSourceType = (sourceType) => {
  if (sourceType === null || sourceType === undefined) return "";
  return sourceType.toString().trim().toLowerCase().replace(/[\s_-]/g, "");
};

const isDependentValueField = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["true", "1", "yes", "si", "y"].includes(
      value.trim().toLowerCase()
    );
  }

  return false;
};

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return "";
  return value.toString().trim().toLowerCase();
};

const hasValidParentValue = (value) => {
  if (value === null || value === undefined || value === "") return false;

  const raw = value.toString().trim();
  if (raw === "" || normalizeComparableValue(raw) === NULL_GUID) return false;

  return raw
    .split(",")
    .some(
      (item) =>
        item &&
        item.trim() !== "" &&
        normalizeComparableValue(item) !== NULL_GUID
    );
};

const sanitizeFilterValue = (value) => {
  if (!hasValidParentValue(value)) return "";

  const raw = value.toString().trim();
  const firstValue = raw
    .split(",")
    .find(
      (item) =>
        item &&
        item.trim() !== "" &&
        normalizeComparableValue(item) !== NULL_GUID
    );

  return firstValue ? firstValue.trim() : "";
};

const normalizeListRecords = (items = []) =>
  (items || []).map((item) => ({
    id: item?.id || item?.Id || item?.primaryKey || item?.PrimaryKey || "",
    description:
      item?.description ||
      item?.Description ||
      item?.label ||
      item?.Label ||
      "",
  }));

const hasAnySelectedValue = (value) => {
  if (Array.isArray(value)) {
    return value.some(
      (item) => normalizeComparableValue(item) !== "" && normalizeComparableValue(item) !== NULL_GUID
    );
  }

  const normalizedValue = normalizeComparableValue(value);
  return normalizedValue !== "" && normalizedValue !== NULL_GUID;
};

const getNormalizedSelectedValues = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeComparableValue(item))
      .filter((item) => item !== "" && item !== NULL_GUID);
  }

  if (value === null || value === undefined) return [];

  return value
    .toString()
    .split(",")
    .map((item) => normalizeComparableValue(item))
    .filter((item) => item !== "" && item !== NULL_GUID);
};

const buildSelectedValuesCsv = (value) => {
  const selectedValues = Array.isArray(value) ? value : value?.toString?.().split(",");

  return (selectedValues || [])
    .map((item) => item?.toString?.().trim())
    .filter((item) => item && item !== "" && normalizeComparableValue(item) !== NULL_GUID)
    .join(",");
};

const getParentFieldValueFromFields = (
  allFields,
  fieldValues,
  customTableColumnName
) => {
  if (!allFields || !customTableColumnName) return "";

  const normalizedColumnName = normalizeComparableValue(customTableColumnName);
  const parentField = allFields.find(
    (field) =>
      normalizeComparableValue(field?.fieldName) === normalizedColumnName ||
      normalizeComparableValue(field?.fieldId) === normalizedColumnName
  );

  if (!parentField) return "";

  return fieldValues?.[parentField.fieldId] ?? parentField.dataField ?? "";
};

const InputsField = ({
  CampoDinamico,
  IdRelacionado,
  onChange = () => { },
  SectionId,
  SubModuloId,
  Elemento = "",
  SubEntityId = null,
  isVisible,
  parentFieldValue = "",
  allFields = [],
  fieldValues = {},
}) => {
  const [{ sesionUsuario, etiquetasIdioma }, dispatch] = useStateValue();

  //variable de estado local para las propiedades del componente
  const [campoX, setCampoX] = useState(CampoDinamico);
  const [listado, setListado] = useState([]);
  const [check, setCheck] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMoreList, setLoadingMoreList] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const lastParentValueRef = useRef(undefined);
  const fetchSequenceRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const listboxRef = useRef(null);
  const appendScrollStateRef = useRef({ previousTop: 0, previousHeight: 0 });
  const parentFieldColumnValue = getParentFieldValueFromFields(
    allFields,
    fieldValues,
    campoX?.customTableColumnName
  );

  useEffect(() => {
    ObtenerLista();
  }, [
    IdRelacionado,
    SubEntityId,
    CampoDinamico?.fieldId,
    campoX?.typeField,
    campoX?.listRecords,
    campoX?.customTableId,
    campoX?.customTableColumnName,
    campoX?.dyfSourceType,
    campoX?.sourceType,
    campoX?.isValoresDependientes,
    parentFieldColumnValue,
    parentFieldValue,
    SubModuloId,
  ]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const notifyChange = (value) => {
    onChange(value);
  };

  const getParentFieldValueByColumnName = (customTableColumnName) => {
    return getParentFieldValueFromFields(
      allFields,
      fieldValues,
      customTableColumnName
    );
  };

  const getSystemTableContext = () => {
    const sourceType = campoX.dyfSourceType || campoX.sourceType;
    const isSystemTableSource = normalizeSourceType(sourceType) === "systemtable";
    const isListType = campoX.typeField === "LIST" || campoX.typeField === "MULT_LIST";
    const isDependentField = isDependentValueField(campoX.isValoresDependientes);
    const parentValueFromColumn =
      isSystemTableSource && isListType
        ? getParentFieldValueByColumnName(campoX.customTableColumnName)
        : parentFieldValue;
    const effectiveParentValue = isDependentField ? parentValueFromColumn : "";

    return {
      isSystemTableSource,
      isListType,
      isDependentField,
      effectiveParentValue,
      parentHasValidValue: hasValidParentValue(effectiveParentValue),
      filterValue: isDependentField ? sanitizeFilterValue(effectiveParentValue) : "",
    };
  };

  const clearCurrentFieldValue = () => {
    if (!hasAnySelectedValue(campoX.dataField)) return;

    setCampoX((anterior) => ({
      ...anterior,
      dataField: "",
    }));
    CampoDinamico.dataField = "";
    notifyChange("");
  };

  const syncCurrentValueWithOptions = (options = []) => {
    if (!hasAnySelectedValue(campoX.dataField)) return;

    const availableIds = new Set(
      options.map((item) => normalizeComparableValue(item.id))
    );

    if (campoX.typeField === "MULT_LIST") {
      const selectedValues = getNormalizedSelectedValues(campoX.dataField);
      const validValues = selectedValues.filter((item) => availableIds.has(item));

      if (validValues.length !== selectedValues.length) {
        const nextValue = validValues.join(",");
        setCampoX((anterior) => ({
          ...anterior,
          dataField: nextValue,
        }));
        CampoDinamico.dataField = nextValue;
        notifyChange(nextValue);
      }
      return;
    }

    if (!availableIds.has(normalizeComparableValue(campoX.dataField))) {
      clearCurrentFieldValue();
    }
  };

  const mergeOptions = (currentOptions = [], incomingOptions = []) => {
    const registry = new Map();

    (currentOptions || []).forEach((option) => {
      registry.set(normalizeComparableValue(option.id), option);
    });

    (incomingOptions || []).forEach((option) => {
      const key = normalizeComparableValue(option.id);
      if (!registry.has(key)) {
        registry.set(key, option);
      }
    });

    return Array.from(registry.values());
  };

  const loadSystemTableRecords = async ({
    requestSequence,
    searchTextValue = "",
    targetPageNumber = 1,
    append = false,
  }) => {
    const context = getSystemTableContext();

    if (context.isDependentField && !context.parentHasValidValue) {
      setListado([]);
      setHasMoreOptions(false);
      setPageNumber(1);
      return;
    }

    const objectRequest = {
      fieldId: campoX.fieldId,
      filterValue: context.filterValue,
      searchText: searchTextValue,
      selectedValues: buildSelectedValuesCsv(campoX.dataField),
      pageNumber: targetPageNumber,
      pageSize: SYSTEM_TABLE_PAGE_SIZE,
      peticionUsuario: {
        companyId: sesionUsuario.usuario.companyId,
        userId: sesionUsuario.usuario.userId,
        optionId: SubModuloId,
        action: AccionesUsuario.Consultar,
      },
    };

    const response = await ObtenerListadoRegistrosTablaSistema(objectRequest);
    if (requestSequence !== fetchSequenceRef.current) return;

    if (response.status === 200) {
      const normalizedOptions = normalizeListRecords(response.data);
      setListado((previous) =>
        append ? mergeOptions(previous, normalizedOptions) : normalizedOptions
      );
      setPageNumber(targetPageNumber);
      setHasMoreOptions(normalizedOptions.length === SYSTEM_TABLE_PAGE_SIZE);

      if (append && listboxRef.current) {
        requestAnimationFrame(() => {
          if (!listboxRef.current) return;
          listboxRef.current.scrollTop = Math.max(
            appendScrollStateRef.current.previousTop - 1,
            0
          );
        });
      }
    } else {
      if (response.data?.errors) {
        const arrayErrores = enlistaArrayCodigosMensajesIdiomas(response.data.errors);
        MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
      } else {
        MostrarMensaje(dispatch, "error", [response.statusText]);
      }

      if (!append) {
        setListado([]);
      }
      setHasMoreOptions(false);
    }
  };

  const ObtenerLista = async () => {
    const requestSequence = ++fetchSequenceRef.current;
    setLoadingList(true);

    if (
      campoX.typeField === "LIST" ||
      campoX.typeField === "MULT_LIST" ||
      campoX.typeField === "RADIO"
    ) {
      const context = getSystemTableContext();
      const normalizedParentValue = sanitizeFilterValue(context.effectiveParentValue);
      //console.log("context1", context);
      if (context.isDependentField) {
        const isFirstParentRead = lastParentValueRef.current === undefined;

        if (isFirstParentRead) {
          lastParentValueRef.current = normalizedParentValue;
        } else if (lastParentValueRef.current !== normalizedParentValue) {
          const previousParentValue = lastParentValueRef.current;
          lastParentValueRef.current = normalizedParentValue;
          const isHydrationTransition =
            previousParentValue === "" &&
            normalizedParentValue !== "" &&
            hasAnySelectedValue(campoX.dataField);

          if (!isHydrationTransition) {
            setListado([]);
            clearCurrentFieldValue();
          }
        }
      } else {
        lastParentValueRef.current = undefined;
      }

      if (
        context.isSystemTableSource &&
        context.isDependentField &&
        !context.parentHasValidValue
      ) {
        setListado([]);
        setHasMoreOptions(false);
        setPageNumber(1);
        setSearchText("");
        setLoadingList(false);
        return;
      }
      //console.log('pase'); //quitar !
      if (context.isSystemTableSource && context.isListType) {
        setSearchText("");
        await loadSystemTableRecords({
          requestSequence,
          searchTextValue: "",
          targetPageNumber: 1,
          append: false,
        });
        setLoadingList(false);
        return;
      }

      const nextCampo = {
        ...campoX,
        dataField: campoX.dataField?.toString?.().toLowerCase?.() ?? "",
      };
      setCampoX(nextCampo);
      const response = await ObtenerListadoRegistrosGenericos(
        campoX.listRecords,
        "",
        sesionUsuario.usuario.companyId,
        sesionUsuario.usuario.userId,
        "",
        true
      );
      if (requestSequence !== fetchSequenceRef.current) return;

      if (response.status === 200) {
        const normalizedOptions = normalizeListRecords(response.data);
        setListado(normalizedOptions);
        syncCurrentValueWithOptions(normalizedOptions);
      } else {
        if (response.data.errors) {
          const arrayErrores = enlistaArrayCodigosMensajesIdiomas(
            response.data.errors
          );
          MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
        } else {
          MostrarMensaje(dispatch, "error", [response.statusText]);
        }
        setListado([]);
      }
    }
    // else if (campoX.typeField === 'RADIO') {
    //     campoX.dataField = campoX.dataField.toUpperCase();
    //     setCampoX(campoX);
    //     // const Params = {
    //     //     SectionId: SectionId,
    //     //     FileName: campoX.fieldName,
    //     //     IdRel: IdRelacionado,
    //     //     PeticionUsuario: {
    //     //         CompanyId: sesionUsuario.usuario.companyId,
    //     //         UserId: sesionUsuario.usuario.userId,
    //     //         OptionId: SubModuloId,
    //     //         Action: AccionesUsuario.Consultar
    //     //     }
    //     // };
    //     // const response = await ObtenerListadoCamposDinamicosRadios(Params);
    //     // if (response.status === 200) //si la respuesta fue existosa
    //     // {
    //     //     //console.log("radios", response.data);
    //     //     setListado(response.data[0]?.fieldList);
    //     // } else {
    //     //     if (response.data.errors) {
    //     //         const arrayErrores = enlistaArrayCodigosMensajesIdiomas(response.data.errors);
    //     //         MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
    //     //     } else {
    //     //         MostrarMensaje(dispatch, 'error', [response.statusText]);
    //     //     }
    //     //     setListado([]);
    //     // }
    //     const response = await ObtenerListadoRegistrosGenericos(campoX.listRecords, '', sesionUsuario.usuario.companyId, sesionUsuario.usuario.userId, '', true, campoX.customTableId, campoX.customTableColumnName, customEntityId)
    //     if (response.status === 200) {
    //         setListado(response.data);
    //     } else {
    //         if (response.data.errors) {

    //             const arrayErrores = enlistaArrayCodigosMensajesIdiomas(response.data.errors);
    //             //revisado ok
    //             MostrarMensaje(dispatch, response.data.typeError, arrayErrores);

    //         } else {
    //             MostrarMensaje(dispatch, 'error', [response.statusText]);
    //         }
    //         setListado([]);
    //     }
    // }
    else if (campoX.typeField === "CHECK") {
      setCheck(campoX.dataField === "1");
    }
    setLoadingList(false);
  };

  const loadMoreSystemTableRecords = async () => {
    if (loadingList || loadingMoreList || !hasMoreOptions) return;

    const context = getSystemTableContext();
    if (context.isDependentField && !context.parentHasValidValue) return;

    const requestSequence = ++fetchSequenceRef.current;
    const nextPage = pageNumber + 1;

    setLoadingMoreList(true);
    await loadSystemTableRecords({
      requestSequence,
      searchTextValue: searchText,
      targetPageNumber: nextPage,
      append: true,
    });
    setLoadingMoreList(false);
  };

  const handleSystemTableSearchChange = (inputValue) => {
    setSearchText(inputValue);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(async () => {
      const requestSequence = ++fetchSequenceRef.current;
      setLoadingList(true);
      await loadSystemTableRecords({
        requestSequence,
        searchTextValue: inputValue,
        targetPageNumber: 1,
        append: false,
      });
      setLoadingList(false);
    }, 350);
  };

  const handleSystemTableScroll = async (event) => {
    const listboxNode = event.currentTarget;
    listboxRef.current = listboxNode;
    const scrollThreshold = 48;
    const isNearBottom =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - scrollThreshold;

    if (isNearBottom) {
      appendScrollStateRef.current = {
        previousTop: listboxNode.scrollTop,
        previousHeight: listboxNode.scrollHeight,
      };
      await loadMoreSystemTableRecords();
    }
  };

  const handleSingleSystemTableChange = (_, selectedOption) => {
    const selectedValue = selectedOption?.id || "";
    setSearchText("");

    setCampoX((anterior) => ({
      ...anterior,
      dataField: selectedValue,
    }));

    CampoDinamico.dataField = selectedValue;
    CampoDinamico.isVisible = isVisible;
    notifyChange(selectedValue);
  };

  const handleMultiSystemTableChange = (_, selectedOptions) => {
    const nextValues = (selectedOptions || [])
      .map((item) => item.id)
      .filter((item) => item !== "");
    const valueString = nextValues.join(",");
    setSearchText("");

    setCampoX((anterior) => ({
      ...anterior,
      dataField: valueString,
    }));

    CampoDinamico.dataField = valueString;
    CampoDinamico.isVisible = isVisible;
    notifyChange(valueString);
  };

  const systemTableContext = getSystemTableContext();
  const isSystemTableAutocomplete =
    systemTableContext.isSystemTableSource && systemTableContext.isListType;
  const disableSystemTableAutocomplete =
    systemTableContext.isDependentField && !systemTableContext.parentHasValidValue;

  return (
    <React.Fragment>
      {campoX.typeField === "CHECK" ? (
        <div
          style={{
            padding: "0px",
            margin: "0px",
            width: "100%",
            display: "inline-block",
          }}
        >
          {creaComponenteInput(
            campoX,
            setCampoX,
            CampoDinamico,
            listado,
            loadingList,
            loadingMoreList,
            searchText,
            isSystemTableAutocomplete,
            disableSystemTableAutocomplete,
            handleSystemTableSearchChange,
            handleSystemTableScroll,
            handleSingleSystemTableChange,
            handleMultiSystemTableChange,
            check,
            setCheck,
            IdRelacionado,
            notifyChange,
            etiquetasIdioma
          )}
          <Typography
            variant="subtitle2"
            name={campoX.fieldId}
            style={{
              fontSize: 16,
              marginBottom: 0,
              fontWeight: "normal",
              display: "inline-block",
            }}
          >
            {campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
          </Typography>
        </div>
      ) : (
        creaComponenteInput(
          campoX,
          setCampoX,
          CampoDinamico,
          listado,
          loadingList,
          loadingMoreList,
          searchText,
          isSystemTableAutocomplete,
          disableSystemTableAutocomplete,
          handleSystemTableSearchChange,
          handleSystemTableScroll,
          handleSingleSystemTableChange,
          handleMultiSystemTableChange,
          check,
          setCheck,
          IdRelacionado,
          notifyChange,
          etiquetasIdioma
        )
      )}
    </React.Fragment>
  );
};

const creaComponenteInput = (
  campoX,
  setCampoX,
  CampoDinamico,
  listFiels,
  loadingList,
  loadingMoreList,
  searchText,
  isSystemTableAutocomplete,
  disableSystemTableAutocomplete,
  handleSystemTableSearchChange,
  handleSystemTableScroll,
  handleSingleSystemTableChange,
  handleMultiSystemTableChange,
  check,
  setCheck,
  IdRelacionado = null,
  notifyChange = () => { },
  etiquetasIdioma
) => {
  const etiquetas = etiquetasIdioma?.EtiquetasComponente || {};
  const [formatoFecha, setFormatoFecha] = useState("dd/MM/yyyy");
  const mensajeCampoPadre =
    etiquetas?.CamposDinamicos?.Et_MsgSelectParentField ||
    "Seleccione un valor para el campo padre.";

  useEffect(() => {
    setFormatoFecha(getFormatDateLocalStorage());
  }, []);

  const ingresarValoresMemoria = (e) => {
    const { value } = e.target; // se desestructura del targen el nombre y el valor que trae
    // se usa el hook de useState sobre la variable de estado local y se actualiza solo la propiedad que concuerda con el
    // valor que se le envia lo demas se queda igual
    setCampoX((anterior) => ({
      ...anterior,
      ["dataField"]: value,
    }));
    CampoDinamico.dataField = value;
    notifyChange(value);
  };

  const ingresarValoresMemoriaMultiList = (e) => {
    const { value } = e.target; // se desestructura del targen el nombre y el valor que trae

    // const finalValue = typeof value === 'string' ? value.split(',') : value;
    const finalValue = value.filter((item) => item !== "");

    setCampoX((anterior) => ({
      ...anterior,
      ["dataField"]: finalValue,
    }));

    const valueString = finalValue.join(",");
    CampoDinamico.dataField = valueString;
    notifyChange(valueString);
  };

  const ingresarValoresMemoriaCheck = (e) => {
    const { checked } = e.target; // se desestructura del targen el nombre y el valor que trae
    // se usa el hook de useState sobre la variable de estado local y se actualiza solo la propiedad que concuerda con el
    // valor que se le envia lo demas se queda igual
    setCampoX((anterior) => ({
      ...anterior,
      ["dataField"]: checked,
    }));
    setCheck(checked);
    CampoDinamico.dataField = checked.toString();
    notifyChange(checked.toString());
  };

  const ingresarValoresMemoriaFecha = (e) => {
    let newValueCampo = e === null ? "" : e;
    setCampoX((anterior) => ({
      ...anterior,
      ["dataField"]: newValueCampo,
    }));
    CampoDinamico.dataField = newValueCampo;
    notifyChange(newValueCampo);
  };

  const ingresarValoresMemoriaNumericos = (e) => {
    const { value } = e.target; // se desestructura del targen el nombre y el valor que trae

    let valorfinal = value;

    // se usa el hook de useState sobre la variable de estado local y se actualiza solo la propiedad que concuerda con el
    // valor que se le envia lo demas se queda igual
    setCampoX((anterior) => ({
      ...anterior,
      ["dataField"]: valorfinal,
    }));
    CampoDinamico.dataField = valorfinal;
    notifyChange(valorfinal);
  };

  if (campoX.typeField === "SHT_TEXT") {
    return campoX.pattern !== "" ? (
      <InputMask
        id={campoX.fieldId}
        name={campoX.fieldName}
        mask={campoX.pattern}
        value={campoX.dataField}
        onChange={ingresarValoresMemoria}
        disabled={false}
        maskChar=" "
      >
        {() => (
          <TextField
            /*required={campoX.isRequired}*/
            variant="outlined"
            fullWidth
            label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
            inputProps={{ maxLength: campoX.maxlength }}
          />
        )}
      </InputMask>
    ) : (
      <TextField
        id={campoX.fieldId}
        name={campoX.fieldName}
        value={campoX.dataField}
        onChange={ingresarValoresMemoria}
        /*required={campoX.isRequired}*/
        label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        variant="outlined"
        fullWidth
        inputProps={{ maxLength: campoX.maxlength }}
      />
    );
  } else if (campoX.typeField === "EMAIL") {
    return (
      <TextField
        name={campoX.fieldName}
        /*required={campoX.isRequired}*/
        id={campoX.fieldId}
        value={campoX.dataField}
        onChange={ingresarValoresMemoria}
        variant="outlined"
        fullWidth
        label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        inputProps={{ maxLength: campoX.maxlength }}
      />
    );
  } else if (campoX.typeField === "LRG_TEXT") {
    return (
      <TextField
        /*required={campoX.isRequired}*/
        id={campoX.fieldId}
        name={campoX.fieldName}
        value={campoX.dataField}
        variant="outlined"
        multiline
        rows={2}
        fullWidth
        label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        onChange={ingresarValoresMemoria}
        inputProps={{
          maxLength: campoX.maxlength,
        }}
      ></TextField>
    );
  } else if (campoX.typeField === "FIX_TEXT") {
    return (
      <Typography
        id={campoX.fieldId}
        variant="caption"
        fullWidth
        style={{
          fontSize: 16,
          color: "rgba(0, 0, 0, 0.60)",
          fontWeight: 400,
        }}
      >
        {campoX.fieldDescrp}
      </Typography>
    );
  } else if (campoX.typeField === "WHL_NUM") {
    return (
      <TextField
        name={campoX.fieldName}
        /*required={campoX.isRequired}*/
        id={campoX.fieldId}
        type="number"
        value={campoX.dataField}
        onChange={ingresarValoresMemoriaNumericos}
        variant="outlined"
        fullWidth
        label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        InputProps={{
          inputProps: { min: campoX.minimum, max: campoX.maximum },
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    );
  } else if (campoX.typeField === "DEC_NUM") {
    return (
      <TextField
        name={campoX.fieldName}
        /*required={campoX.isRequired}*/
        id={campoX.fieldId}
        type="number"
        value={campoX.dataField}
        onChange={ingresarValoresMemoriaNumericos}
        variant="outlined"
        fullWidth
        label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        InputProps={{
          inputProps: {
            min: campoX.minimum,
            max: campoX.maximum,
            step: campoX.decimal,
          },
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    );
  } else if (campoX.typeField === "DATE") {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        locale={idioma === "ES" ? es : ""}
      >
        <DesktopDatePicker
          format="YYYY-MM-DD"
          /*required={campoX.isRequired}*/
          inputFormat={formatoFecha}
          id={campoX.fieldId}
          name={campoX.fieldName}
          value={campoX.dataField === "" ? null : campoX.dataField}
          variant="outlined"
          label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
          onChange={(newValue) => {
            ingresarValoresMemoriaFecha(newValue);
          }}
          renderInput={(params) => <TextField fullWidth {...params} />}
        />
        {/*required={campoX.isRequired}*/}
        {/*minDate={new Date('1938-01-01')}*/}
        {/*maxDate={new Date()}*/}
      </LocalizationProvider>
    );
  } else if (campoX.typeField === "HOUR") {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          id={campoX.fieldId}
          /*required={campoX.isRequired}*/
          name={campoX.fieldName}
          value={campoX.dataField === "" ? null : campoX.dataField}
          label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
          onChange={(newValue) => {
            ingresarValoresMemoriaFecha(newValue);
          }}
          renderInput={(params) => <TextField fullWidth {...params} />}
        />
      </LocalizationProvider>
    );
  } else if (campoX.typeField === "RADIO") {
    // return
    // <FormControl fullWidth variant="outlined">
    //     <RadioGroup id={campoX.fieldId} name={campoX.fieldName} value={campoX.dataField} onChange={ingresarValoresMemoria} row>
    //         {
    //             listFiels.map((record) => (<FormControlLabel key={"ident-" + record.fieldId} value={record.value.toUpperCase()} control={<Radio />} label={record.fieldDescrp} />))
    //         }
    //     </RadioGroup>
    // </FormControl>
    return (
      <FormControl
        fullWidth
        variant="outlined"
        required={campoX.isRequired}
        sx={{
          position: "relative",
          width: "100%",
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "4px",
          padding: "16px 14px 8px",
          boxSizing: "border-box",
          "&:hover": {
            borderColor: "rgba(0, 0, 0, 0.87)",
          },
        }}
      >
        <FormLabel
          id={`radio-${campoX.fieldId}-label`}
          sx={{
            position: "absolute",
            top: "-12px",
            left: "8px",
            backgroundColor: "white",
            padding: "0 4px",
            fontSize: "0.875rem",
          }}
        >
          {campoX.fieldDescrp}
        </FormLabel>
        <RadioGroup
          id={campoX.fieldId}
          name={campoX.fieldName}
          value={campoX.dataField}
          onChange={ingresarValoresMemoria}
          row
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            width: "100%",
            "& .MuiFormControlLabel-root": {
              margin: 0,
              minWidth: "auto",
              flex: "1 1 auto",
            },
          }}
        >
          {listFiels.map((record) => (
            <FormControlLabel
              key={"ident-" + record.id}
              value={record.id}
              control={<Radio />}
              label={record.description}
              sx={{
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                },
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  } else if (campoX.typeField === "CHECK") {
    return (
      <Checkbox
        id={campoX.fieldId}
        checked={check}
        onChange={ingresarValoresMemoriaCheck}
        color="primary"
        inputProps={{ "aria-label": "secondary checkbox" }}
      />
    );
  } else if (campoX.typeField === "LIST") {
    if (isSystemTableAutocomplete) {
      const currentValue = normalizeComparableValue(campoX.dataField);
      const selectedOption =
        listFiels.find(
          (option) => normalizeComparableValue(option.id) === currentValue
        ) || null;

      return (
        <Autocomplete
          id={campoX.fieldId}
          options={listFiels}
          value={selectedOption}
          loading={loadingList || loadingMoreList}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              handleSystemTableSearchChange(value || "");
            }
          }}
          onChange={handleSingleSystemTableChange}
          getOptionLabel={(option) => option?.description || ""}
          isOptionEqualToValue={(option, value) =>
            normalizeComparableValue(option?.id) ===
            normalizeComparableValue(value?.id)
          }
          filterOptions={(options) => {
            const normalizedSearch = normalizeComparableValue(searchText);
            if (normalizedSearch === "") return options;

            return options.filter((option) =>
              normalizeComparableValue(option?.description).includes(
                normalizedSearch
              )
            );
          }}
          disabled={disableSystemTableAutocomplete}
          ListboxProps={{
            onScroll: handleSystemTableScroll,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required={campoX.isRequired}
              label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
              helperText={disableSystemTableAutocomplete ? mensajeCampoPadre : ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingList || loadingMoreList ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    return (
      <FormControl
        fullWidth
        variant="outlined"
        disabled={listFiels.length === 0 && isDependentValueField(campoX.isValoresDependientes)}
      >
        <InputLabel id={campoX.fieldId + "-label"}>
          {campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        </InputLabel>
        <Select
          id={campoX.fieldId}
          /*required={campoX.isRequired}*/
          name={campoX.fieldName}
          value={campoX.dataField || ""}
          onChange={ingresarValoresMemoria}
          label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
          labelId={campoX.fieldId + "-label"}
        >
          {listFiels.map((record) => (
            <MenuItem key={"ident-" + record.id} value={record.id}>
              {record.description}
            </MenuItem>
          ))}
        </Select>
        {listFiels.length === 0 && isDependentValueField(campoX.isValoresDependientes) ? (
          <FormHelperText>{mensajeCampoPadre}</FormHelperText>
        ) : null}
      </FormControl>
    );
  } else if (campoX.typeField === "MULT_LIST") {
    if (isSystemTableAutocomplete) {
      const selectedValues = getNormalizedSelectedValues(campoX.dataField);
      const selectedSet = new Set(selectedValues);
      const selectedOptions = listFiels.filter((option) =>
        selectedSet.has(normalizeComparableValue(option.id))
      );

      return (
        <Autocomplete
          id={campoX.fieldId}
          multiple
          options={listFiels}
          value={selectedOptions}
          loading={loadingList || loadingMoreList}
          inputValue={searchText}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              handleSystemTableSearchChange(value || "");
            }
          }}
          onChange={handleMultiSystemTableChange}
          getOptionLabel={(option) => option?.description || ""}
          isOptionEqualToValue={(option, value) =>
            normalizeComparableValue(option?.id) ===
            normalizeComparableValue(value?.id)
          }
          filterOptions={(options) => options}
          disabled={disableSystemTableAutocomplete}
          disableCloseOnSelect
          ListboxProps={{
            onScroll: handleSystemTableScroll,
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
              {option?.description || ""}
            </li>
          )}
          renderTags={() => `${selectedValues.length} seleccionado(s)`}
          renderInput={(params) => (
            <TextField
              {...params}
              required={campoX.isRequired}
              label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
              helperText={disableSystemTableAutocomplete ? mensajeCampoPadre : ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingList || loadingMoreList ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    //al crear el campo, el valor inicial es siempre un string y es necesario un array en el value, por eso se le hace split.
    const arrayValue = (campoX.dataField || "").toString().split(",");

    return (
      <FormControl
        fullWidth
        variant="outlined"
        disabled={listFiels.length === 0 && isDependentValueField(campoX.isValoresDependientes)}
      >
        <InputLabel id={campoX.fieldId + "-label"}>
          {campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
        </InputLabel>
        <Select
          id={campoX.fieldId}
          /*required={campoX.isRequired}*/
          labelId={campoX.fieldId + "-label"}
          multiple
          name={campoX.fieldName}
          value={arrayValue}
          onChange={ingresarValoresMemoriaMultiList}
          // label={campoX.fieldDescrp + (campoX.isRequired ? ' *' : '')}
          input={
            <OutlinedInput
              label={campoX.fieldDescrp + (campoX.isRequired ? " *" : "")}
            />
          }
          renderValue={(selected) =>
            listFiels
              .filter((item) => selected.includes(item.id))
              .map((item) => item.description)
              .join(", ")
          }
        >
          {listFiels.map((record) => (
            <MenuItem key={"ident-" + record.id} value={record.id}>
              <Checkbox checked={arrayValue.indexOf(record.id) > -1} />
              <ListItemText primary={record.description} />
            </MenuItem>
          ))}
        </Select>
        {listFiels.length === 0 && isDependentValueField(campoX.isValoresDependientes) ? (
          <FormHelperText>{mensajeCampoPadre}</FormHelperText>
        ) : null}
      </FormControl>
    );
  } else if (campoX.typeField === "DOC") {
    return (
      <BaseFieldUpload
        campoX={campoX}
        ownerEntity={IdRelacionado}
        onChange={ingresarValoresMemoria}
        formatsAllowed=".pdf,.doc,.docx"
      />
    );
  } else if (campoX.typeField === "IMG") {
    return (
      <BaseFieldUpload
        campoX={campoX}
        ownerEntity={IdRelacionado}
        onChange={ingresarValoresMemoria}
        formatsAllowed="image/*"
      />
    );
  }
};

const BaseFieldUpload = ({
  campoX,
  ownerEntity,
  onChange,
  formatsAllowed = "*/*",
}) => {
  const [{ etiquetasIdioma, sesionUsuario }, dispatch] = useStateValue();

  const {
    fieldId,
    fieldName,
    fieldDescrp,
    isRequired,
    dataField: value,
  } = campoX;

  const [selectedFile, setSelectedFile] = useState(null);

  const [isWaiting, setIsWaiting] = useState(false);

  // const handleFileChange = (event) => {
  //     const file = event.target.files[0];
  //     setSelectedFile(file);
  // };

  const guardarDataArchivo = async (e) => {
    const fileInfo = await RutinaLeerArchivoInputFile(e);
    const CodigoValidacion = "Attach_dynamic_fields_";
    if (CodigoValidacion !== "") {
      const dataObject = {
        ElementId: ownerEntity,
        MimeType: fileInfo.tipo,
        ValidateCode: CodigoValidacion,
      };

      ValidarFormatoArchivo(dataObject).then((response) => {
        if (response.status === 200) {
          //si la respuesta fue existosa
          if (response.data === true) {
            setSelectedFile(fileInfo);
            const { name, value } = e.target;
            const newEvent = { target: { name, value: fileInfo.nombre } };

            onChange(newEvent);
          } else {
            // InicializarDialogo();
            setSelectedFile(null);
            MostrarMensaje(dispatch, "warning", ["FormartFileError"]);
          }
        } else {
          if (response.data.errors) {
            const arrayErrores = enlistaArrayCodigosMensajesIdiomas(
              response.data.errors
            );
            //revisado ok
            MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
          } else {
            MostrarMensaje(dispatch, "error", [response.statusText]);
          }
        }
      });
    }
  };

  const SubirArchivoServidor = async (e) => {
    setIsWaiting(true); // Mostrar loading antes de la petición

    const dataObject = {
      FileType: selectedFile.tipo,
      FileContent: selectedFile.contenido,
      FileSize: selectedFile.tamano,
      FileName: selectedFile.nombre,
      ObjectId: ownerEntity,
      FolderName: "DynamicFields",
      CompanyId: sesionUsuario.usuario.companyId,
      UserId: sesionUsuario.usuario.userId,
    };

    try {
      const response = await SubirArchivosServidor(dataObject);
      if (response.status === 201) {
        MostrarMensaje(dispatch, "success", [
          "Archivo subido satisfactoriamente.",
        ]);
      } else {
        eliminarAdjunto({});
        if (response.status === 401) {
          MostrarMensaje(dispatch, "error", ["ErrorFile401"]);
        } else if (response.status === 409) {
          MostrarMensaje(dispatch, "warning", ["ErrorFile409"]);
        } else if (response.status === 500) {
          MostrarMensaje(dispatch, "error", ["InternalServerError"]);
        } else if (response.data.errors) {
          const arrayErrores = enlistaArrayCodigosMensajesIdiomas(
            response.data.errors
          );
          MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
        } else {
          MostrarMensaje(dispatch, "error", [response.statusText]);
        }
      }
    } catch (error) {
      MostrarMensaje(dispatch, "error", [
        error.message || "Error al subir archivo",
      ]);
    } finally {
      setIsWaiting(false); // Ocultar loading después de la petición
    }
  };

  const descargarArchivo = () => {
    setIsWaiting(true); // Mostrar loading antes de la petición

    const dataObject = {
      FileName: value ?? selectedFile?.nombre,
      FolderName: "DynamicFields",
      ObjectId: ownerEntity,
      CompanyId: sesionUsuario.usuario.companyId,
      UserId: sesionUsuario.usuario.userId,
    };

    BajarArchivosServidor(dataObject)
      .then((response) => {
        if (response.status === 200) {
          DescargarArchivoBase64(
            dataObject.FileName,
            "",
            "data:" + null + ";base64," + response.data
          );
        } else if (response.data.errors) {
          const arrayErrores = enlistaArrayCodigosMensajesIdiomas(
            response.data.errors
          );
          MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
        } else {
          MostrarMensaje(dispatch, "error", [response.statusText]);
        }
      })
      .finally(() => {
        setIsWaiting(false); // Ocultar loading después de la petición
      });
  };

  const eliminarArchivoServidor = async (e) => {
    const dataObject = {
      FileName: value ?? selectedFile?.nombre,
      FolderName: "DynamicForms",
      ObjectId: ownerEntity,
      CompanyId: sesionUsuario.usuario.companyId,
      UserId: sesionUsuario.usuario.userId,
    };
    setIsWaiting(true);
    try {
      const response = await EliminarArchivosServidor(dataObject);
      if (response.status === 200) {
        eliminarAdjunto(e);
        MostrarMensaje(dispatch, "success", [
          "Archivo eliminado satisfactoriamente.",
        ]);
      } else if (response.data.errors) {
        const arrayErrores = enlistaArrayCodigosMensajesIdiomas(
          response.data.errors
        );
        MostrarMensaje(dispatch, response.data.typeError, arrayErrores);
      } else {
        MostrarMensaje(dispatch, "error", [response.statusText]);
      }
    } catch (error) {
      MostrarMensaje(dispatch, "error", [
        error.message || "Error al eliminar archivo",
      ]);
    } finally {
      setIsWaiting(false);
    }
  };

  const eliminarAdjunto = (e = {}) => {
    setSelectedFile(null);
    const newEvent = { ...e, target: { name: fieldName, value: "" } };
    // guardarDataArchivo(newEvent);
    onChange(newEvent);
  };

  useEffect(() => {
    if (selectedFile !== null) {
      SubirArchivoServidor();
    }
  }, [selectedFile]);

  return (
    <>
      <input
        type="file"
        accept={formatsAllowed ?? "*/*"}
        style={{ display: "none" }}
        id={"hidden-image-input_" + fieldName}
        // onChange={handleFileChange}
        onChange={guardarDataArchivo}
        name={fieldName}
      />
      <TextField
        fullWidth
        name={fieldName}
        label={fieldDescrp}
        value={selectedFile ? selectedFile.nombre : value}
        required={isRequired}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isWaiting ? (
                <CircularProgress color="inherit" />
              ) : selectedFile !== null || value !== "" ? (
                <>
                  <Tooltip title="Descargar Adjunto">
                    <IconButton
                      component="span"
                      color="primary"
                      aria-label="subir archivo"
                      onClick={descargarArchivo}
                    >
                      {/* <FileDownloadIcon /> */}
                      <CloudDownloadOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar Adjunto">
                    <IconButton
                      component="span"
                      color="primary"
                      aria-label="subir archivo"
                      onClick={(e) => eliminarArchivoServidor(e)}
                    >
                      <ClearOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <label htmlFor={"hidden-image-input_" + fieldName}>
                  <Tooltip title="Subir Adjunto">
                    <IconButton
                      component="span"
                      color="primary"
                      aria-label="subir archivo"
                    >
                      {/* <UploadFileIcon /> */}
                      {/* <CloudDownloadIcon /> */}
                      <BackupOutlinedIcon />
                      {/* <AttachFileIcon /> */}
                    </IconButton>
                  </Tooltip>
                </label>
              )}
            </InputAdornment>
          ),
        }}
        variant="outlined"
        disabled
      />
    </>
  );
};

export default InputsField;
